// Web Audio API — task completion sound effects

let audioCtx = null;

const getAudioContext = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

// ─── Pencil "scritch-scratch" ───

const stroke = (ctx, start, dur, vol, freq, q, hpFreq) => {
  const len = Math.floor(ctx.sampleRate * dur);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    const t = i / len;
    const env = t < 0.03 ? t / 0.03 : t > 0.7 ? (1 - t) / 0.3 : 1;
    d[i] = (Math.random() * 2 - 1) * env;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass'; bp.frequency.value = freq; bp.Q.value = q;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass'; hp.frequency.value = hpFreq;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(vol, start);
  src.connect(bp); bp.connect(hp); hp.connect(gain); gain.connect(ctx.destination);
  src.start(start); src.stop(start + dur);
};

const pencilCheck = () => {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  stroke(ctx, now, 0.08, 0.3, 3000, 0.8, 800);
  stroke(ctx, now + 0.14, 0.1, 0.25, 5500, 2.5, 2000);
};

const pencilUncheck = () => {
  const ctx = getAudioContext();
  stroke(ctx.currentTime, 0.1, 0.18, 2500, 0.7, 600);
};

// ─── Ping! — bright, bell-like ───

const pingCheck = () => {
  const ctx = getAudioContext();
  const now = ctx.currentTime;

  // "Plink" — sharp metallic tap, like flicking a small glass or key
  const osc1 = ctx.createOscillator();
  const g1 = ctx.createGain();
  osc1.type = 'triangle';
  osc1.frequency.setValueAtTime(3200, now);
  osc1.frequency.exponentialRampToValueAtTime(2600, now + 0.03);
  g1.gain.setValueAtTime(0.25, now);
  g1.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  osc1.connect(g1); g1.connect(ctx.destination);
  osc1.start(now); osc1.stop(now + 0.15);

  // Second harmonic — gives the metallic "ink" ring
  const osc2 = ctx.createOscillator();
  const g2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(5200, now);
  g2.gain.setValueAtTime(0.08, now);
  g2.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  osc2.connect(g2); g2.connect(ctx.destination);
  osc2.start(now); osc2.stop(now + 0.1);
};

const pingUncheck = () => {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(2400, now);
  osc.frequency.exponentialRampToValueAtTime(1600, now + 0.08);
  g.gain.setValueAtTime(0.18, now);
  g.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  osc.connect(g); g.connect(ctx.destination);
  osc.start(now); osc.stop(now + 0.12);
};

// ─── Pop! — snappy bubble burst ───

const popCheck = () => {
  const ctx = getAudioContext();
  const now = ctx.currentTime;

  // Sharp transient click — the snap
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.008), ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
  const click = ctx.createBufferSource();
  click.buffer = buf;
  const cg = ctx.createGain();
  cg.gain.setValueAtTime(0.3, now);
  click.connect(cg); cg.connect(ctx.destination);
  click.start(now);

  // Resonant body — fast pitch drop gives the "pop" character
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(1200, now);
  osc.frequency.exponentialRampToValueAtTime(180, now + 0.06);
  g.gain.setValueAtTime(0.3, now);
  g.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  osc.connect(g); g.connect(ctx.destination);
  osc.start(now); osc.stop(now + 0.1);
};

const popUncheck = () => {
  const ctx = getAudioContext();
  const now = ctx.currentTime;

  // Softer, lower reverse-pop
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(350, now);
  osc.frequency.exponentialRampToValueAtTime(150, now + 0.07);
  g.gain.setValueAtTime(0.2, now);
  g.gain.exponentialRampToValueAtTime(0.001, now + 0.09);
  osc.connect(g); g.connect(ctx.destination);
  osc.start(now); osc.stop(now + 0.09);
};

// ─── Public API ───

const sounds = {
  ping:   { check: pingCheck,   uncheck: pingUncheck },
  pop:    { check: popCheck,    uncheck: popUncheck },
  pencil: { check: pencilCheck, uncheck: pencilUncheck },
};

export const playCheckSound = () => {
  try {
    const enabled = localStorage.getItem('soundEnabled') !== 'false';
    if (!enabled) return;
    const type = localStorage.getItem('soundType') || 'pencil';
    (sounds[type] || sounds.pencil).check();
  } catch (e) { /* silent */ }
};

export const playUncheckSound = () => {
  try {
    const enabled = localStorage.getItem('soundEnabled') !== 'false';
    if (!enabled) return;
    const type = localStorage.getItem('soundType') || 'pencil';
    (sounds[type] || sounds.pencil).uncheck();
  } catch (e) { /* silent */ }
};

/** Preview a sound type (for settings UI) */
export const previewSound = (type) => {
  try {
    (sounds[type] || sounds.pencil).check();
  } catch (e) { /* silent */ }
};
